import {
  SecretLinksService,
  SecretLinkUrlDto,
} from '@ts/services/secretLinksService';
import { pingMatomo } from '../analytics';

const disableButton = () => {
  const button = document.querySelector<HTMLButtonElement>(
    '.js-share-secret-link',
  );
  if (!button) return;

  button.disabled = true;
  button.style.borderColor = '#8a8a8a';
};

const getRemainingText = (count: number) => {
  return count
    ? `Diesen Monat können Sie noch ${count} Artikel verschenken.`
    : 'Sie haben in diesem Monat schon fünf Artikel verschenkt.';
};

const addRemainingSecretLinksCountText = (
  count: number,
  hasValidLink: boolean,
) => {
  const messageNode = document.querySelector<HTMLElement>(
    '.js-secret-links-modal-remaining',
  );
  if (!messageNode) return;

  messageNode.dataset.count = `${count}`;

  const text = getRemainingText(count);

  messageNode.textContent = text;

  if (count <= 0 && !hasValidLink) {
    disableButton();
  }
};

const getRemainingSecretLinksCount = async (
  secretLinksService: SecretLinksService,
) => {
  try {
    const secretLink = await secretLinksService.getRemainingSecretLinksCount();

    addRemainingSecretLinksCountText(
      secretLink.count,
      secretLink.has_valid_link,
    );
  } catch (error) {
    if (error instanceof Error) {
      console.error(
        `Error getting secret links count for subscriber: ${error.message}`,
      );

      const messageNode = document.querySelector<HTMLElement>(
        '.js-secret-links-modal-remaining',
      );

      if (!messageNode) return;
      messageNode.style.minHeight = '10px';
    }
  }
};

const removeFeedbackText = () => {
  document.querySelector('.js-secret-links-modal-feedback')?.remove();

  const copyButton = document.querySelector('.js-share-secret-link');
  copyButton?.classList.remove('o-copy-success', 'o-copy-error');
};

const addCurrentFeedbackText = (status: string) => {
  removeFeedbackText();

  const messageNode = document.createElement('div');
  messageNode.classList.add('js-secret-links-modal-feedback');

  const copyButton = document.querySelector('.js-share-secret-link');

  let text: string;
  if (status === 'success' || status === 'created') {
    text =
      'Sie haben den Link erfolgreich in die Zwischenablage kopiert. Sie können ihn nun überall einfügen.';
    copyButton?.classList.add('o-copy-success');
    messageNode.setAttribute('data-testid', 'success');
  } else {
    text =
      'Leider können wir Ihre Anfrage gerade nicht verarbeiten. Bitte versuchen Sie es später erneut.';
    copyButton?.classList.add('o-copy-error');
    messageNode.setAttribute('data-testid', 'error');
  }

  const textElement = document.createTextNode(text);
  messageNode.appendChild(textElement);

  copyButton?.after(messageNode);
};

const updateRemainingSecretLinksCountText = (status: string) => {
  if (status === 'created') {
    const remaining = document.querySelector<HTMLElement>(
      '.js-secret-links-modal-remaining',
    );

    const count = remaining?.dataset.count;
    if (!count) return;

    const countNumber = Number(count);

    remaining.textContent =
      countNumber > 1 ? getRemainingText(countNumber - 1) : getRemainingText(0);
  }
};

const copyUrlToClipboard = (url: string) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url);
  } else {
    // Clipboard API not working/available - use fallback method
    const textarea = document.createElement('textarea');
    textarea.value = url;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
};

const handleSecretLinkUrl = (secretLink: SecretLinkUrlDto) => {
  if (secretLink.status === 'success' || secretLink.status === 'created') {
    const { status, url } = secretLink;

    copyUrlToClipboard(url);
    addCurrentFeedbackText(status);
    updateRemainingSecretLinksCountText(status);

    return;
  }

  if (secretLink.status === 'error') {
    const { status, message } = secretLink;

    addCurrentFeedbackText(status);

    console.error(`Error getting secret link for subscriber: ${message}`);
  }
};

export const secretLinksDialog = async (
  secretLinksService = new SecretLinksService(),
) => {
  const dialogElement = document.querySelector('dialog.bc-giveaway');
  if (!(dialogElement instanceof HTMLElement)) return;

  const giftButtons = document.querySelectorAll('button.js-open-dialog');

  const shareSecretLinkButton = dialogElement.querySelector(
    '.js-share-secret-link',
  );

  /**
   * The `Copy To Clipboard` functionality can be a bit tricky depending on the browser.
   *
   * For some browsers (eg. Safari), this action is better handled synchronously.
   *
   * Solution: attach the SecretLink's URL fetch call to the `openModalButton` click event,
   * keeping the `copy to clipboard` functionality synchronous.
   */
  giftButtons?.forEach(giftButton => {
    giftButton.addEventListener('click', async () => {
      pingMatomo('gift');

      getRemainingSecretLinksCount(secretLinksService);
      const secretLinkUrl: SecretLinkUrlDto =
        await secretLinksService.getSecretLinkUrl();

      shareSecretLinkButton?.addEventListener('click', () => {
        handleSecretLinkUrl(secretLinkUrl);
      });
    });
  });
};
